/**
 * Page „Meldefristen“ (DEV-137) — pure view-model helpers.
 *
 * Positions on the shared time axis are percentages of the visible window, so
 * the rows stay plain HTML/CSS and scale with the page. Nothing here does I/O.
 */

import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';

import { backendLabel } from '../../lib/backendLabels';
import { intlLocale } from '../../lib/locale';
import type { CaseClock, TimelineCase, TimelineMilestone } from '../../lib/types';

export type TimelineRange = '14' | '42' | 'all';
export type TimelineScope = 'open' | 'all';
/** `due`: overdue or due within 24 h — the dashboard tile „Fällige Meldefristen“ (DEV-133). */
export type TimelineFocus = 'overdue' | 'due_24h' | 'due' | 'waiting' | null;

/** Labels are translated at render with `i18n._()`. */
export const RANGE_OPTIONS: ReadonlyArray<{ value: TimelineRange; label: MessageDescriptor }> = [
  { value: '14', label: msg`2 Wochen` },
  { value: '42', label: msg`6 Wochen` },
  { value: 'all', label: msg`Alles` },
];

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export interface TimeWindow {
  start: number;
  end: number;
}

function ms(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Every point in time a case shows: first hit, awareness, deadlines, stamps, milestones. */
function caseTimes(row: TimelineCase): number[] {
  const values = [
    row.first_hit_at,
    row.aware_since,
    row.corrective_measure.available_at,
    ...row.clocks.flatMap((clock) => [clock.due, clock.submitted_at]),
    ...row.milestones.map((milestone) => milestone.at),
  ];
  return values.map(ms).filter((value): value is number => value !== null);
}

/**
 * The visible window. A fixed range looks a few days back and mostly ahead —
 * the focus is what is still coming. „Alles“ spans every point of the rows.
 */
export function timeWindow(range: TimelineRange, rows: readonly TimelineCase[], now: number): TimeWindow {
  if (range !== 'all') {
    const days = Number(range);
    const back = Math.round(days / 5);
    return { start: now - back * DAY, end: now + (days - back) * DAY };
  }
  const times = [now, ...rows.flatMap(caseTimes)];
  return { start: Math.min(...times) - DAY, end: Math.max(...times) + DAY };
}

/** Percentage of the window, or null when the point lies outside it. */
export function position(at: string | null | undefined, window: TimeWindow): number | null {
  const value = ms(at);
  if (value === null || value < window.start || value > window.end) return null;
  return ((value - window.start) / (window.end - window.start)) * 100;
}

/** A stretch of time clipped to the window: left and width in percent; null when not visible. */
export function span(
  from: string | null | undefined, to: string | null | undefined, window: TimeWindow,
): { left: number; width: number } | null {
  const start = ms(from);
  const end = ms(to);
  if (start === null || end === null || end < window.start || start > window.end) return null;
  const left = ((Math.max(start, window.start) - window.start) / (window.end - window.start)) * 100;
  const right = ((Math.min(end, window.end) - window.start) / (window.end - window.start)) * 100;
  return { left, width: Math.max(right - left, 0.4) };
}

/** Day ticks of the axis: daily for two weeks, weekly beyond, never more than about ten. */
export function axisTicks(window: TimeWindow): Array<{ at: number; left: number; label: string }> {
  const days = (window.end - window.start) / DAY;
  const step = days <= 16 ? 1 : Math.max(7, Math.ceil(days / 10 / 7) * 7);
  const first = new Date(window.start);
  first.setHours(0, 0, 0, 0);
  const ticks: Array<{ at: number; left: number; label: string }> = [];
  for (let at = first.getTime() + DAY; at < window.end; at += step * DAY) {
    const date = new Date(at);
    ticks.push({
      at,
      left: ((at - window.start) / (window.end - window.start)) * 100,
      label: date.toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit' }),
    });
  }
  return ticks;
}

/** Hours as „42 h“ or, from two days on, „3 Tage“ — in the UI language. */
export function hoursLabel(hours: number): string {
  const value = Math.abs(hours);
  if (value >= 48) {
    const days = Math.round(value / 24);
    return t`${plural(days, { one: '# Tag', other: '# Tage' })}`;
  }
  const digits = value < 10 ? 1 : 0;
  return `${value.toLocaleString(intlLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })} h`;
}

/** Clocks not reported yet that run (or ran out); the waiting final report has no deadline. */
export function openClocks(row: TimelineCase): CaseClock[] {
  return row.clocks.filter((clock) => clock.state === 'open' || clock.state === 'due_soon' || clock.state === 'overdue');
}

/** The one line telling where the case stands — also the text form of the row. */
export function standing(row: TimelineCase): string {
  if (row.status === 'OBSOLETE') return t`Entfallen: keine Version mehr betroffen`;
  const overdue = row.clocks.filter((clock) => clock.state === 'overdue');
  if (overdue.length > 0) {
    return overdue.map((clock) => {
      const label = backendLabel(clock.label);
      const duration = hoursLabel(clock.remaining_hours ?? 0);
      return t`${label} seit ${duration} überschritten`;
    }).join(' · ');
  }
  const next = openClocks(row)[0];
  if (next) {
    const label = backendLabel(next.label);
    const duration = hoursLabel(next.remaining_hours ?? 0);
    return t`${label} fällig in ${duration}`;
  }
  if (row.clocks.some((clock) => clock.state === 'waiting')) return t`Frühwarnung und Meldung erfasst · wartet auf Abhilfemaßnahme`;
  return t`Alle Meldungen erfasst`;
}

export function isClosed(row: TimelineCase): boolean {
  return row.status === 'COMPLETED' || row.status === 'OBSOLETE';
}

function dueWithin(clock: CaseClock, hours: number): boolean {
  return (clock.state === 'open' || clock.state === 'due_soon')
    && clock.remaining_hours !== null && clock.remaining_hours <= hours;
}

export function matchesFocus(row: TimelineCase, focus: TimelineFocus): boolean {
  if (focus === null) return true;
  if (isClosed(row)) return false;
  if (focus === 'overdue') return row.clocks.some((clock) => clock.state === 'overdue');
  if (focus === 'due_24h') return row.clocks.some((clock) => dueWithin(clock, 24));
  if (focus === 'due') return row.clocks.some((clock) => clock.state === 'overdue' || dueWithin(clock, 24));
  return row.clocks.some((clock) => clock.state === 'waiting');
}

export interface TimelineFilters {
  search: string;
  scope: TimelineScope;
  product: string;
  focus: TimelineFocus;
}

export function matchesFilters(row: TimelineCase, filters: TimelineFilters): boolean {
  if (filters.scope === 'open' && isClosed(row)) return false;
  if (filters.product !== '' && row.product_name !== filters.product) return false;
  if (!matchesFocus(row, filters.focus)) return false;
  const query = filters.search.trim().toLowerCase();
  return query === '' || [row.cve, row.product_name ?? ''].some((value) => value.toLowerCase().includes(query));
}

/** Event texts of the backend (EVENT_LABELS), appended to the clock label of a milestone. */
const EVENT_SUFFIXES: Readonly<Record<string, MessageDescriptor>> = {
  läuft: msg`läuft`,
  'läuft bald ab': msg`läuft bald ab`,
  überschritten: msg`überschritten`,
};

/**
 * A milestone text in the active UI language. The backend composes these as
 * German sentences (deadline_timeline_service.py), so they are matched by kind
 * and pattern until DEV-158 delivers keys; an unknown text is shown as it came.
 */
export function milestoneLabel(milestone: TimelineMilestone): string {
  const { kind, label } = milestone;
  if (kind === 'hit') return t`KEV-Treffer erkannt`;
  if (kind === 'aware') return t`Kenntnis (X): Betroffen bestätigt`;
  if (kind === 'corrective') return t`Abhilfemaßnahme verfügbar (Y)`;
  if (kind === 'mail') {
    if (label === 'Erste Kundenmail versendet') return t`Erste Kundenmail versendet`;
    if (label === 'Letzte Kundenmail versendet') return t`Letzte Kundenmail versendet`;
  }
  if (kind === 'report' && label.endsWith(' gemeldet')) {
    const clock = backendLabel(label.slice(0, -' gemeldet'.length));
    return t`${clock} gemeldet`;
  }
  if (kind === 'advisory') {
    const version = /^Advisory (.*) freigegeben$/.exec(label)?.[1];
    if (version !== undefined) return version === '' ? t`Advisory freigegeben` : t`Advisory ${version} freigegeben`;
  }
  if (kind === 'event') {
    const suffix = Object.keys(EVENT_SUFFIXES).find((german) => label.endsWith(` ${german}`));
    if (suffix !== undefined) {
      const clock = backendLabel(label.slice(0, -(suffix.length + 1)));
      const event = i18n._(EVENT_SUFFIXES[suffix]!);
      return `${clock} ${event}`;
    }
  }
  return label;
}
